import { httpClient } from "../http-client";
import {
  Transaction,
  BudgetItem,
  PlatformBudgetSummary,
  BudgetPriority,
  BudgetStatus,
  TransactionType,
  TransactionStatus,
  BudgetCategory,
  Payment,
  PaymentStatus,
  PaymentMethod,
  Contract,
} from "../../types/shared-types";
import {
  ApiResponse,
  PaginatedResponse,
} from "../../types/responses/api-response";

// ===== ДОКУМЕНТЫ И ОТЧЕТЫ API =====

export const documentsApi = {
  // Получение списка отчетов
  getReports: async (params?: {
    page?: number;
    limit?: number;
    status?: "draft" | "completed" | "failed" | "generating";
    category?: string;
    period?: string;
    search?: string;
  }): Promise<ApiResponse<PaginatedResponse<any>>> => {
    return httpClient.get("/documents/reports", { params });
  },

  // Создание нового отчета
  createReport: async (data: {
    name: string;
    category: string;
    period: string;
    format: "pdf" | "excel" | "csv";
    currency: string;
    periodStart: string;
    periodEnd: string;
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/documents/reports", data);
  },

  // Генерация отчета
  generateReport: async (id: string): Promise<ApiResponse<any>> => {
    return httpClient.post(`/documents/reports/${id}/generate`);
  },

  // Получение ссылки для скачивания отчета
  downloadReport: async (
    id: string,
  ): Promise<ApiResponse<{ downloadUrl: string; expiresAt: string }>> => {
    return httpClient.post(`/documents/reports/${id}/download`);
  },

  // Получение информации об отчете
  getReport: async (id: string): Promise<ApiResponse<any>> => {
    return httpClient.get(`/documents/reports/${id}`);
  },

  // Получение шаблонов отчетов
  getReportTemplates: async (params?: {
    page?: number;
    limit?: number;
    format?: string;
    locale?: string;
    country?: string;
    category?: string;
    isActive?: boolean;
    search?: string;
  }): Promise<ApiResponse<PaginatedResponse<any>>> => {
    return httpClient.get("/documents/reports/templates", { params });
  },

  // Создание шаблона отчета
  createReportTemplate: async (data: {
    name: string;
    description: string;
    format: string;
    locale: string;
    country: string;
    templateContent: string;
    templateSettings: any;
    category: string;
    isActive: boolean;
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/documents/reports/templates", data);
  },

  // Получение шаблона по умолчанию
  getDefaultReportTemplate: async (
    locale: string,
  ): Promise<ApiResponse<any>> => {
    return httpClient.get(`/documents/reports/templates/default/${locale}`);
  },

  // Обновление шаблона отчета
  updateReportTemplate: async (
    id: string,
    data: {
      name: string;
      description: string;
      format: string;
      locale: string;
      country: string;
      templateContent: string;
      templateSettings: any;
      category: string;
      isActive: boolean;
    },
  ): Promise<ApiResponse<any>> => {
    return httpClient.put(`/documents/reports/templates/${id}`, data);
  },

  // Удаление шаблона отчета
  deleteReportTemplate: async (id: string): Promise<ApiResponse<any>> => {
    return httpClient.delete(`/documents/reports/templates/${id}`);
  },

  // Получение сводки по отчетам
  getReportsSummary: async (): Promise<
    ApiResponse<{
      totalReports: number;
      completedReports: number;
      draftReports: number;
      failedReports: number;
      reportsByType: Record<string, number>;
      reportsByStatus: Record<string, number>;
    }>
  > => {
    return httpClient.get("/documents/reports/summary/overview");
  },
};

// ===== КОНТРАКТЫ API =====

export const contractsApi = {
  // Получение списка контрактов
  getContracts: async (params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
  }): Promise<ApiResponse<PaginatedResponse<Contract>>> => {
    return httpClient.get("/documents/contracts", { params });
  },

  // Получение контракта по ID
  getContract: async (id: string): Promise<ApiResponse<Contract>> => {
    return httpClient.get(`/documents/contracts/${id}`);
  },

  // Создание нового контракта
  createContract: async (data: {
    title: string;
    description?: string;
    type: string;
    amount: number;
    currency: string;
    startDate: string;
    endDate?: string;
    counterparty: string;
    terms?: string;
  }): Promise<ApiResponse<Contract>> => {
    return httpClient.post("/documents/contracts", data);
  },

  // Обновление контракта
  updateContract: async (
    id: string,
    data: Partial<Contract>,
  ): Promise<ApiResponse<Contract>> => {
    return httpClient.put(`/documents/contracts/${id}`, data);
  },

  // Удаление контракта
  deleteContract: async (id: string): Promise<ApiResponse<void>> => {
    return httpClient.delete(`/documents/contracts/${id}`);
  },

  // Обновление статуса контракта
  updateContractStatus: async (
    id: string,
    status: string,
  ): Promise<ApiResponse<Contract>> => {
    return httpClient.put(`/documents/contracts/${id}/status`, { status });
  },

  // Получение сводки по контрактам
  getContractsSummary: async (): Promise<
    ApiResponse<{
      totalContracts: number;
      activeContracts: number;
      expiredContracts: number;
      totalValue: number;
      contractsByType: Record<string, number>;
      contractsByStatus: Record<string, number>;
    }>
  > => {
    return httpClient.get("/documents/contracts/summary/overview");
  },
};

// ===== СЧЕТА API =====

export const invoicesApi = {
  // Получение списка счетов
  getInvoices: async (params?: {
    page?: number;
    limit?: number;
    customer?: string;
    status?: string;
    type?: string;
  }): Promise<ApiResponse<PaginatedResponse<any>>> => {
    return httpClient.get("/documents/invoices", { params });
  },

  // Создание нового счета
  createInvoice: async (data: {
    invoiceNumber: string;
    customer: string;
    supplier: string;
    amount: number;
    currency: string;
    type: string;
    issueDate: string;
    dueDate: string;
    description: string;
    items: Array<{
      name: string;
      quantity: number;
      unitPrice: number;
      total: number;
    }>;
    createdBy: string;
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/documents/invoices", data);
  },

  // Отправка счета
  sendInvoice: async (id: string): Promise<ApiResponse<any>> => {
    return httpClient.post(`/documents/invoices/${id}/send`);
  },

  // Отметка счета как оплаченного
  markInvoiceAsPaid: async (id: string): Promise<ApiResponse<any>> => {
    return httpClient.post(`/documents/invoices/${id}/mark-as-paid`);
  },

  // Скачивание счета
  downloadInvoice: async (
    id: string,
  ): Promise<ApiResponse<{ downloadUrl: string; expiresAt: string }>> => {
    return httpClient.get(`/documents/invoices/${id}/download`);
  },

  // Получение шаблонов счетов
  getInvoiceTemplates: async (): Promise<ApiResponse<any[]>> => {
    return httpClient.get("/documents/invoices/templates");
  },

  // Создание шаблона счета
  createInvoiceTemplate: async (data: {
    name: string;
    description: string;
    locale: string;
    country: string;
    defaultCurrency: string;
    templateContent: string;
    templateSettings: any;
    defaultFields: any;
    defaultTaxRate: number;
    defaultSupplier: string;
    category: string;
    isActive: boolean;
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/documents/invoices/templates", data);
  },

  // Получение сводки по счетам
  getInvoicesSummary: async (): Promise<
    ApiResponse<{
      totalInvoices: number;
      paidInvoices: number;
      unpaidInvoices: number;
      overdueInvoices: number;
      totalAmount: number;
      paidAmount: number;
      unpaidAmount: number;
      invoicesByType: Record<string, number>;
      invoicesByStatus: Record<string, number>;
    }>
  > => {
    return httpClient.get("/documents/invoices/summary/overview");
  },
};

// ===== РЕФЕРАЛЬНАЯ СИСТЕМА API =====

export const referralsApi = {
  // Получение реферального профиля
  getReferralProfile: async (userId: string): Promise<ApiResponse<any>> => {
    return httpClient.get("/referrals/profile", { params: { userId } });
  },

  // Создание реферального профиля
  createReferralProfile: async (data: {
    userId: string;
    referralCode: string;
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/referrals/profile", data);
  },

  // Получение всех реферальных заработков (для администраторов)
  getAllReferralEarnings: async (params: {
    status?: string;
    type?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<any>>> => {
    return httpClient.get("/referrals/earnings/admin", { params });
  },

  // Получение реферальных заработков пользователя
  getReferralEarnings: async (params: {
    userId: string;
    status?: string;
    type?: string;
  }): Promise<ApiResponse<PaginatedResponse<any>>> => {
    return httpClient.get("/referrals/earnings", { params });
  },

  // Создание реферального заработка
  createReferralEarning: async (data: {
    userId: string;
    type: string;
    amount: number;
    currency: string;
    description: string;
    commissionRate: number;
    referredUserId: string;
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/referrals/earnings", data);
  },

  // Одобрение реферального заработка
  approveReferralEarning: async (id: string): Promise<ApiResponse<any>> => {
    return httpClient.put(`/referrals/earnings/${id}/approve`);
  },

  // Отклонение реферального заработка
  rejectReferralEarning: async (
    id: string,
    reason: string,
  ): Promise<ApiResponse<any>> => {
    return httpClient.put(`/referrals/earnings/${id}/reject`, { reason });
  },

  // Выплата реферального заработка
  payReferralEarning: async (id: string): Promise<ApiResponse<any>> => {
    return httpClient.put(`/referrals/earnings/${id}/pay`);
  },

  // Получение реферальных приглашений
  getReferralInvites: async (params: {
    userId: string;
    status?: string;
  }): Promise<ApiResponse<PaginatedResponse<any>>> => {
    return httpClient.get("/referrals/invites", { params });
  },

  // Создание реферального приглашения
  createReferralInvite: async (data: {
    userId: string;
    email: string;
    message: string;
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/referrals/invites", data);
  },

  // Принятие реферального приглашения
  acceptReferralInvite: async (id: string): Promise<ApiResponse<any>> => {
    return httpClient.post(`/referrals/invites/${id}/accept`);
  },

  // Отклонение реферального приглашения
  rejectReferralInvite: async (
    id: string,
    reason: string,
  ): Promise<ApiResponse<any>> => {
    return httpClient.post(`/referrals/invites/${id}/reject`, { reason });
  },

  // Получение списка приглашенных пользователей
  getReferrals: async (): Promise<ApiResponse<any[]>> => {
    return httpClient.get("/referrals/referrals");
  },

  // Получение статистики реферальной программы (бэкенд часто ожидает userId в query)
  getReferralStats: async (userId?: string): Promise<ApiResponse<any>> => {
    return httpClient.get(
      "/referrals/stats",
      userId ? { params: { userId } } : undefined,
    );
  },

  // Обновление настроек реферальной программы
  updateReferralSettings: async (data: {
    autoPayout: boolean;
    payoutThreshold: number;
    preferredPayoutMethod: string;
    notifications: {
      newReferral: boolean;
      earnings: boolean;
      payout: boolean;
    };
  }): Promise<ApiResponse<any>> => {
    return httpClient.put("/referrals/settings", data);
  },

  // Получение информации об уровнях реферальной программы
  getReferralLevels: async (): Promise<ApiResponse<any[]>> => {
    return httpClient.get("/referrals/levels");
  },

  // Запрос выплаты
  requestPayout: async (data: {
    userId: string;
    amount: number;
    method: string;
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/referrals/payouts/request", data);
  },

  // Получение истории выплат
  getPayouts: async (): Promise<ApiResponse<any[]>> => {
    return httpClient.get("/referrals/payouts");
  },
};

// ===== ФИНАНСОВАЯ АНАЛИТИКА API =====

export const financialAnalyticsApi = {
  // Получение метрик финансовой аналитики
  getMetrics: async (params: {
    userId: string;
    metricType?: string;
    period?: string;
  }): Promise<ApiResponse<PaginatedResponse<any>>> => {
    return httpClient.get("/financial-analytics/metrics", { params });
  },

  // Создание метрики финансовой аналитики
  createMetric: async (data: {
    userId: string;
    metricType: string;
    period: string;
    startDate: string;
    endDate: string;
    data: Array<{
      date: string;
      value: number;
      currency: string;
    }>;
    summary: {
      total: number;
      average: number;
      min: number;
      max: number;
      growth: number;
      trend: string;
      currency: string;
    };
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/financial-analytics/metrics", data);
  },

  // Получение финансовых инсайтов
  getInsights: async (params: {
    userId: string;
    metric?: string;
    type?: string;
  }): Promise<ApiResponse<any[]>> => {
    return httpClient.get("/financial-analytics/insights", { params });
  },

  // Генерация финансовых инсайтов
  generateInsights: async (data: {
    userId: string;
    metricType: string;
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/financial-analytics/insights/generate", data);
  },

  // Получение финансовых прогнозов
  getPredictions: async (params: {
    userId: string;
    metric?: string;
    timeframe?: string;
  }): Promise<ApiResponse<any[]>> => {
    return httpClient.get("/financial-analytics/predictions", { params });
  },

  // Генерация финансовых прогнозов
  generatePredictions: async (data: {
    userId: string;
    metric: string;
    timeframe: string;
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/financial-analytics/predictions/generate", data);
  },

  // Получение списка дашбордов
  getDashboards: async (userId: string): Promise<ApiResponse<any[]>> => {
    return httpClient.get("/financial-analytics/dashboards", {
      params: { userId },
    });
  },

  // Создание дашборда
  createDashboard: async (data: {
    userId: string;
    name: string;
    description: string;
    isPublic: boolean;
  }): Promise<ApiResponse<any>> => {
    return httpClient.post("/financial-analytics/dashboards", data);
  },

  // Получение дашборда по ID
  getDashboard: async (id: string): Promise<ApiResponse<any>> => {
    return httpClient.get(`/financial-analytics/dashboards/${id}`);
  },

  // Обновление дашборда
  updateDashboard: async (id: string, data: any): Promise<ApiResponse<any>> => {
    return httpClient.put(`/financial-analytics/dashboards/${id}`, data);
  },

  // Удаление дашборда
  deleteDashboard: async (id: string): Promise<ApiResponse<void>> => {
    return httpClient.delete(`/financial-analytics/dashboards/${id}`);
  },

  // Добавление виджета в дашборд
  addWidget: async (
    dashboardId: string,
    data: {
      type: string;
      title: string;
      description: string;
      metricType: string;
      period: string;
      config: any;
      position: {
        x: number;
        y: number;
        w: number;
        h: number;
      };
    },
  ): Promise<ApiResponse<any>> => {
    return httpClient.post(
      `/financial-analytics/dashboards/${dashboardId}/widgets`,
      data,
    );
  },

  // Получение трендов
  getTrends: async (params?: {
    metric?: string;
    period?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<any[]>> => {
    return httpClient.get("/financial-analytics/trends", { params });
  },

  // Получение отчетов.
  // Контракт BE (financial-analytics.controller.ts:281 `GET /financial-analytics/reports`):
  // принимает query `type`/`period` (НЕ page/limit/format) и возвращает ГОЛЫЙ массив
  // отчётов (financial-analytics.service.ts getReports после F-A2 — не PaginatedResponse).
  getReports: async (params?: {
    type?: string;
    period?: string;
  }): Promise<ApiResponse<any[]>> => {
    return httpClient.get("/financial-analytics/reports", { params });
  },
};

// ===== БЮДЖЕТ API =====

/**
 * Ответ `GET /finance/budget/summary` — ровно то, что возвращает
 * `BudgetService.getBudgetSummary` (backend `finance/services/core/budget.service.ts`).
 * Имена здесь СЕРВЕРНЫЕ: отклонение называется `variance`, а `remaining` сервер
 * не считает вовсе. Экраны читали `totalVariance` и `remaining` прямо из этого
 * ответа и получали прочерки (AD-469).
 */
export interface PlatformBudgetSummaryResponse {
  totalPlanned: number;
  totalActual: number;
  variance: number;
  utilization: number;
  currency: string;
  criticalItems: number;
  highPriorityItems: number;
  mediumPriorityItems: number;
  lowPriorityItems: number;
  categories: Array<{
    category: BudgetCategory;
    planned: number;
    actual: number;
    variance: number;
  }>;
}

/**
 * Единственный переход от ответа сервера к тому, что читают экраны.
 *
 * Адаптер один на всех потребителей намеренно: `FinancePage`,
 * `BudgetPlannerPage` и `EventBudgetPage` (когда события нет) ходят под ОДНИМ
 * ключом кэша React Query — `["finance", "budget", "summary"]`. Два разных
 * приведения под одним ключом дали бы разные числа в зависимости от того, чей
 * запрос лёг в кэш первым.
 */
export const adaptPlatformBudgetSummary = (
  raw: PlatformBudgetSummaryResponse,
): PlatformBudgetSummary => ({
  totalPlanned: raw.totalPlanned,
  totalActual: raw.totalActual,
  totalVariance: raw.variance,
  remaining: raw.totalPlanned - raw.totalActual,
  utilization: raw.utilization,
  currency: raw.currency,
  categories: raw.categories?.map((entry) => entry.category),
  criticalItems: raw.criticalItems,
  highPriorityItems: raw.highPriorityItems,
  mediumPriorityItems: raw.mediumPriorityItems,
  lowPriorityItems: raw.lowPriorityItems,
});

export const budgetApi = {
  // AD-469: `/finance/budget` отдаёт НЕ сводку, а последние позиции бюджета
  // (backend `BudgetController.getBudgetOverview`) — тип здесь говорил обратное.
  getBudget: async (): Promise<
    ApiResponse<{ budget: BudgetItem[]; overview: BudgetItem[] }>
  > => {
    return httpClient.get("/finance/budget");
  },
  getBudgetSummary: async (): Promise<
    ApiResponse<PlatformBudgetSummaryResponse>
  > => {
    return httpClient.get("/finance/budget/summary");
  },
  getBudgetItems: async (params?: {
    page?: number;
    limit?: number;
    status?: BudgetStatus;
    priority?: BudgetPriority;
    category?: BudgetCategory;
  }): Promise<ApiResponse<PaginatedResponse<BudgetItem>>> => {
    return httpClient.get("/finance/budget/items", { params });
  },
  createBudgetItem: async (
    data: Partial<BudgetItem>,
  ): Promise<ApiResponse<BudgetItem>> => {
    return httpClient.post("/finance/budget/items", data);
  },
  updateBudgetItem: async (
    id: string,
    data: Partial<BudgetItem>,
  ): Promise<ApiResponse<BudgetItem>> => {
    return httpClient.put(`/finance/budget/items/${id}`, data);
  },
  deleteBudgetItem: async (id: string): Promise<ApiResponse<void>> => {
    return httpClient.delete(`/finance/budget/items/${id}`);
  },
};

// ===== СПИСКИ ПЛАТЕЖЕЙ И ТРАНЗАКЦИЙ: ФОРМА НА ПРОВОДЕ =====

/**
 * AD-604, AD-612: страница списка «Платежей» и «Транзакций» так, как она приходит.
 * Контроллер оборачивает ответ сам — `{ success, data: { items, pagination } }` — и
 * глобальный `ResponseInterceptor` пропускает такую обёртку без изменений, поэтому
 * `PaginatedResponse` (`data` + `meta`) здесь не годится. Экраны читали `data.data` и
 * `data.payments`, оба выражения давали `undefined`, и список был пуст при любом ответе.
 */
export interface FinanceListPage<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Колонки `amount` у `payments` и `transactions` — `decimal` без трансформера (класс
 * AD-197): Postgres отдаёт сумму строкой (`"100.00"`), и `+=` на экране склеивал строки
 * вместо сложения. Приводим к числу здесь, один раз, — экраны получают `number` по типу.
 */
type WireAmount<T extends { amount: number }> = Omit<T, "amount"> & {
  amount: number | string;
};
type WirePayment = WireAmount<Payment>;
type WireTransaction = WireAmount<Transaction>;

const toFinanceListPage = <W, T>(
  body: ApiResponse<FinanceListPage<W>>,
  route: string,
  toItem: (wire: W) => T,
): FinanceListPage<T> => {
  // Чужая форма — ошибка запроса, а не пустой список: пустой список неотличим от
  // «платежей нет», ровно так AD-604 и прятался.
  if (!Array.isArray(body?.data?.items) || !body.data.pagination) {
    throw new Error(`${route}: ответ не в форме { items, pagination }`);
  }
  return {
    items: body.data.items.map(toItem),
    pagination: body.data.pagination,
  };
};

// ===== ТРАНЗАКЦИИ API =====

/**
 * AD-612: `incoming` — поступления ВЫЗЫВАЮЩЕМУ (экран «Доходы»): его транзакции
 * дохода и чужие расходы, где он назван поставщиком. Тип транзакции записан со
 * стороны владельца, поэтому оплата поставщику приходит с типом `expense`.
 * Зеркало `TransactionDirection` сервера (`transaction-filter.dto.ts`).
 */
export type TransactionDirection = "incoming";

export const transactionsApi = {
  getTransactions: async (params?: {
    page?: number;
    limit?: number;
    type?: TransactionType;
    status?: TransactionStatus;
    startDate?: string;
    endDate?: string;
    direction?: TransactionDirection;
  }): Promise<FinanceListPage<Transaction>> => {
    const body: ApiResponse<FinanceListPage<WireTransaction>> =
      await httpClient.get("/finance/transactions", { params });
    return toFinanceListPage(body, "/finance/transactions", (tx) => ({
      ...tx,
      amount: Number(tx.amount),
    }));
  },
  createTransaction: async (
    data: Partial<Transaction>,
  ): Promise<ApiResponse<Transaction>> => {
    return httpClient.post("/finance/transactions", data);
  },
  updateTransaction: async (
    id: string,
    data: Partial<Transaction>,
  ): Promise<ApiResponse<Transaction>> => {
    return httpClient.put(`/finance/transactions/${id}`, data);
  },
  deleteTransaction: async (id: string): Promise<ApiResponse<void>> => {
    return httpClient.delete(`/finance/transactions/${id}`);
  },
};

// ===== ПЛАТЕЖИ API =====

/**
 * AD-604: сводка `GET /finance/payments/summary` — числа считает сервер. Период
 * учитывается, только когда заданы обе даты; отказ базы приходит ошибкой, а не нулями.
 */
export interface PaymentsSummary {
  pendingAmount: number;
  pendingCount: number;
  paidAmount: number;
  paidCount: number;
  overdueAmount: number;
  overdueCount: number;
  totalTurnover: number;
  currency: string;
}

export const paymentsApi = {
  getPaymentsSummary: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<PaymentsSummary>> => {
    return httpClient.get("/finance/payments/summary", { params });
  },
  getPayments: async (params?: {
    page?: number;
    limit?: number;
    status?: PaymentStatus;
    paymentMethod?: PaymentMethod;
    startDate?: string;
    endDate?: string;
  }): Promise<FinanceListPage<Payment>> => {
    const body: ApiResponse<FinanceListPage<WirePayment>> =
      await httpClient.get("/finance/payments", { params });
    return toFinanceListPage(body, "/finance/payments", (payment) => ({
      ...payment,
      amount: Number(payment.amount),
    }));
  },
  createPayment: async (
    data: Partial<Payment>,
  ): Promise<ApiResponse<Payment>> => {
    return httpClient.post("/finance/payments", data);
  },
  updatePayment: async (
    id: string,
    data: Partial<Payment>,
  ): Promise<ApiResponse<Payment>> => {
    return httpClient.put(`/finance/payments/${id}`, data);
  },
  deletePayment: async (id: string): Promise<ApiResponse<void>> => {
    return httpClient.delete(`/finance/payments/${id}`);
  },
};

// ===== ПРОГНОЗЫ API =====

export const forecastApi = {
  getForecast: async (params: {
    period?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<any>> => {
    return httpClient.get("/finance/forecast", { params });
  },
};

// ===== ОТЧЕТЫ API =====

export const reportsApi = {
  exportData: async (params: {
    type: string;
    format: "csv" | "excel" | "pdf";
    startDate?: string;
    endDate?: string;
  }): Promise<Blob> => {
    const response = await httpClient.get("/finance/reports/export", {
      params,
      responseType: "blob",
    });
    return response.data as Blob;
  },
};

// ===== ОСНОВНОЙ FINANCE API ОБЪЕКТ =====

export const financeApi = {
  budget: budgetApi,
  transactions: transactionsApi,
  payments: paymentsApi,
  documents: documentsApi,
  contracts: contractsApi,
  invoices: invoicesApi,
  referrals: referralsApi,
  analytics: financialAnalyticsApi,
  forecast: forecastApi,
  reports: reportsApi,
};

// Экспорт по умолчанию для обратной совместимости
export default financeApi;
